import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'

import styles from './toast.module.scss'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface ToastRequest {
  variant: ToastVariant
  message: string
  durationMs?: number
}

interface ToastState extends ToastRequest {
  id: number
  durationMs: number
}

interface ToastContextValue {
  showToast: (toast: ToastRequest) => void
  dismissToast: () => void
}

interface ToastProps {
  variant: ToastVariant
  message: string
  onClose: () => void
}

const ICONS = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
} as const

const DEFAULT_TOAST_DURATION_MS = 3200
const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null)
  const nextId = useRef(0)

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((request: ToastRequest) => {
    setToast({
      id: nextId.current + 1,
      variant: request.variant,
      message: request.message,
      durationMs: request.durationMs ?? DEFAULT_TOAST_DURATION_MS,
    })
    nextId.current += 1
  }, [])

  useEffect(() => {
    if (!toast || toast.durationMs <= 0) return
    const id = window.setTimeout(() => setToast(null), toast.durationMs)
    return () => window.clearTimeout(id)
  }, [toast])

  const value = useMemo(() => ({ showToast, dismissToast }), [dismissToast, showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast ? (
        <Toast
          key={toast.id}
          variant={toast.variant}
          message={toast.message}
          onClose={dismissToast}
        />
      ) : null}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

/**
 * Fixed bottom-right notification for action feedback.
 * The parent owns the timer and visibility — render this when a message is present,
 * unmount (or set to null) to dismiss.
 */
export function Toast({ variant, message, onClose }: ToastProps) {
  const Icon = ICONS[variant]
  return (
    <div className={styles.toast} data-variant={variant} role="status" aria-live="polite">
      <Icon size={18} className={styles.icon} aria-hidden />
      <span className={styles.message}>{message}</span>
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        aria-label="Chiudi notifica"
      >
        <X size={16} aria-hidden />
      </button>
    </div>
  )
}
